import { useState, useMemo, useEffect } from "react";
import {
  AlertCircle,
  Search,
  Eye,
  User,
  CreditCard,
  Calendar,
} from "lucide-react";
import { Link } from "react-router-dom";
import FinancialChart from "../FinancialChart";
import ServicePaymentModal from "./UI/ServicePaymentModal";
import { updatePaymentService } from "../../../firebase/firebase.new-payments";

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "PYG",
    minimumFractionDigits: 0,
  }).format(amount);
};

const DentalFinancialDashboard = ({ allPayments }) => {
  const [selectedFilter, setSelectedFilter] = useState("todos");
  const [allPaymentsState, setAllPaymentsState] = useState(null);
  const [showGraph, setShowGraph] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedTab, setSelectedTab] = useState("payment");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    if (allPayments) {
      setAllPaymentsState(allPayments);
    }
  }, [allPayments]);

  const filteredData = useMemo(() => {
    let filtered = allPaymentsState;
    if (selectedFilter === "pagados") {
      filtered = filtered.filter((item) => item.hasBeenPaid);
    } else if (selectedFilter === "pendientes") {
      filtered = filtered.filter((item) => !item.hasBeenPaid);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.reason.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [selectedFilter, searchTerm, allPaymentsState]);

  const getPaymentStatus = (item) => {
    if (item.hasBeenPaid) return "paid";
    if (item.daysOverdue > 30) return "overdue";
    if (item.balance > 0) return "partial";
    return "pending";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800 border-green-200";
      case "overdue":
        return "bg-red-100 text-red-800 border-red-200";
      case "partial":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "paid":
        return "Pagado";
      case "overdue":
        return "Vencido";
      case "partial":
        return "Parcial";
      default:
        return "Pendiente";
    }
  };

  const getProgressPercentage = (item) => {
    return ((item.price - item.balance) / item.price) * 100;
  };

  const toggleItemExpansion = (itemId) => {
    setExpandedItems((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId);
      } else {
        newExpanded.add(itemId);
      }
      return newExpanded;
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Open modal with selected service
  const handleOpenPaymentModal = (service, tab = "payment") => {
    setSelectedService(service);
    setSelectedTab(tab);
    setIsModalOpen(true);
  };

  // Handle service updates from modal
  const handleServiceUpdate = async (updatedService) => {
    const success = await updatePaymentService(updatedService);

    if (success) {
      // Update local state to reflect changes
      setAllPaymentsState((prevPayments) =>
        prevPayments.map((payment) =>
          payment.id === updatedService.id ? updatedService : payment
        )
      );
      setIsModalOpen(false);
      setSelectedService(null);
    }
  };

  const pickActionType = (setActionType) => {
    setActionType();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 w-full">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Alerts Section */}
        {metrics && metrics.overdueCount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-800">
                  Pagos Vencidos Detectados
                </h3>
                <p className="text-red-700 text-sm mt-1">
                  Tienes {metrics.overdueCount} tratamiento(s) con pagos
                  vencidos por {formatCurrency(metrics.overdueAmount)}
                </p>
              </div>
              <button className="ml-auto bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">
                Gestionar
              </button>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm md:h-20 items-center flex justify-between">
          <div className="flex flex-col md:flex-row gap-4 w-full">
            <div className="flex gap-2">
              {["todos", "pendientes", "pagados"].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedFilter === filter
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {filter === "todos"
                    ? "Todos"
                    : filter === "pendientes"
                    ? "Pendientes"
                    : "Pagados"}
                </button>
              ))}

              <button
                onClick={() => setShowGraph(!showGraph)}
                className={`rounded-lg w-10 h-10 flex justify-center md:hidden items-center font-medium transition-colors ${
                  searchTerm === "" && showGraph
                    ? "bg-blue-600 text-white"
                    : `bg-gray-100 text-gray-700 hover:bg-gray-200 ${
                        searchTerm !== "" && "opacity-30 cursor-not-allowed"
                      }`
                }`}
                disabled={searchTerm !== ""}
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 3v18h18" />
                  <path d="M18 17V9" />
                  <path d="M13 17V5" />
                  <path d="M8 17v-3" />
                </svg>
              </button>
            </div>
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Buscar paciente o tratamiento..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <button
              onClick={() => setShowGraph(!showGraph)}
              className={`rounded-lg w-10 h-10 flex justify-center hidden md:flex items-center font-medium transition-colors ${
                searchTerm === "" && showGraph
                  ? "bg-blue-600 text-white"
                  : `bg-gray-100 text-gray-700 hover:bg-gray-200 ${
                      searchTerm !== "" && "opacity-30 cursor-not-allowed"
                    }`
              }`}
              disabled={searchTerm !== ""}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 3v18h18" />
                <path d="M18 17V9" />
                <path d="M13 17V5" />
                <path d="M8 17v-3" />
              </svg>
            </button>
          </div>
        </div>

        {/* Enhanced Charts Section */}
        {searchTerm === "" && showGraph && allPaymentsState && (
          <FinancialChart
            financialData={allPaymentsState}
            setMetrics={setMetrics}
          />
        )}

        {/* Treatments List */}
        {filteredData && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Tratamientos
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                {filteredData?.length} tratamientos encontrados
              </p>
            </div>

            <div className="divide-y divide-gray-200">
              {filteredData?.map((item) => {
                const status = getPaymentStatus(item);
                const progress = getProgressPercentage(item);
                const isExpanded = expandedItems.has(item.id);
                return (
                  <div
                    key={item.id}
                    className="border-b border-gray-200 last:border-b-0"
                  >
                    <div className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Patient and Treatment Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="bg-gray-100 p-2 rounded-full">
                              <User className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {item.patientName}
                              </h3>
                              <p className="text-gray-600 text-sm">
                                {item.reason}
                              </p>
                            </div>
                          </div>
                          <p className="text-gray-500 text-sm line-clamp-2">
                            {item.description}
                          </p>
                        </div>

                        {/* Payment Progress */}
                        <div className="lg:w-48">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-gray-600">
                              Progreso
                            </span>
                            <span className="text-sm font-medium">
                              {Math.round(progress)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                status === "paid"
                                  ? "bg-green-500"
                                  : status === "overdue"
                                  ? "bg-red-500"
                                  : status === "partial"
                                  ? "bg-yellow-500"
                                  : "bg-blue-500"
                              }`}
                              style={{ width: `${Math.max(progress, 5)}%` }}
                            />
                          </div>
                        </div>

                        {/* Financial Info */}
                        <div className="lg:w-32 text-right">
                          <p className="font-bold text-gray-900">
                            {formatCurrency(item.price)}
                          </p>
                          {item.balance > 0 && (
                            <p className="text-sm text-red-600">
                              Debe: {formatCurrency(item.balance)}
                            </p>
                          )}
                        </div>

                        {/* Status and Actions */}
                        <div className="lg:w-40 flex flex-col items-end gap-2">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                              status
                            )}`}
                          >
                            {getStatusText(status)}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => toggleItemExpansion(item.id)}
                              className={`p-2 hover:bg-gray-100 rounded-lg transition-colors ${
                                isExpanded
                                  ? "text-blue-600 bg-blue-50"
                                  : "text-gray-400 hover:text-gray-600"
                              }`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {!item.hasBeenPaid && (
                              <button
                                onClick={() => handleOpenPaymentModal(item)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <CreditCard className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          {item.daysOverdue > 0 && (
                            <p className="text-xs text-red-500">
                              {item.daysOverdue} días vencido
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details Section */}
                    {isExpanded && (
                      <div className="px-6 pb-6 bg-gray-50 border-t border-gray-200">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                          {/* Treatment Details */}
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-600" />
                              Detalles del Tratamiento
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Fecha:</span>
                                <span className="font-medium">
                                  {formatDate(item.date)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  Tratamiento:
                                </span>
                                <span className="font-medium">
                                  {item.reason}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  Precio Total:
                                </span>
                                <span className="font-bold text-blue-600">
                                  {formatCurrency(item.price)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pagado:</span>
                                <span className="font-medium text-green-600">
                                  {formatCurrency(item.price - item.balance)}
                                </span>
                              </div>
                              {item.balance > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">
                                    Pendiente:
                                  </span>
                                  <span className="font-bold text-red-600">
                                    {formatCurrency(item.balance)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">
                                  Descripción:
                                </span>{" "}
                                {item.description}
                              </p>
                            </div>
                          </div>

                          {/* Payment History */}
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-gray-600" />
                              Historial de Pagos
                              {!item.hasBeenPaid &&
                                item?.payments?.length > 0 && (
                                  <button
                                    onClick={() => handleOpenPaymentModal(item)}
                                    className="ml-auto text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                                  >
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
                                      <span className="xs:hidden">Pago</span>
                                    </div>
                                  </button>
                                )}
                            </h4>

                            {item?.payments && item?.payments?.length > 0 ? (
                              <div className="space-y-2">
                                {item.payments.map((payment, index) => (
                                  <div
                                    key={payment.id || index}
                                    className="flex justify-between items-start p-3 border-b border-gray-100 last:border-b-0"
                                  >
                                    <div className="w-[75%]">
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatCurrency(payment.amount)}
                                      </p>
                                      <p className="text-xs text-gray-500 mb-4">
                                        {typeof payment.date === "string"
                                          ? formatDate(payment.date)
                                          : payment.date.seconds
                                          ? formatDate(
                                              new Date(
                                                payment.date.seconds * 1000
                                              ).toISOString()
                                            )
                                          : "Fecha no disponible"}
                                      </p>
                                      {payment.description && (
                                        <p className="text-xs text-gray-400 flex flex-wrap ">
                                          {payment.description}
                                        </p>
                                      )}
                                    </div>
                                    <div className="text-right flex items-center">
                                      {payment.invoice && (
                                        <p className="text-xs text-gray-500 mr-2">
                                          #{payment.invoice}
                                        </p>
                                      )}
                                      <div className="w-2 h-2 bg-green-500 rounded-full ml-auto mt-1"></div>
                                    </div>
                                  </div>
                                ))}

                                {/* Payment Summary */}
                                <div className="pt-2 mt-3 border-t border-gray-200">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Total Pagado:
                                    </span>
                                    <span className="font-bold text-green-600">
                                      {formatCurrency(
                                        item.payments.reduce(
                                          (sum, p) => sum + p.amount,
                                          0
                                        )
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <div className="text-gray-400 mb-2">
                                  <CreditCard className="w-8 h-8 mx-auto" />
                                </div>
                                <p className="text-sm text-gray-500">
                                  No hay pagos registrados
                                </p>
                                <button
                                  onClick={() => handleOpenPaymentModal(item)}
                                  className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                                >
                                  Registrar Primer Pago
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="mt-4 flex flex-wrap gap-2">
                          {!item.hasBeenPaid && (
                            <>
                              <button
                                onClick={() =>
                                  handleOpenPaymentModal(item, "markPaid")
                                }
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                Marcar como Pagado
                              </button>
                              {item.daysOverdue > 0 && (
                                <button className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded text-sm transition-colors">
                                  Enviar Recordatorio
                                </button>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => handleOpenPaymentModal(item, "edit")}
                            className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1 rounded text-sm transition-colors"
                          >
                            Editar Tratamiento
                          </button>
                          <Link
                            to={`/${item.patientId}`}
                            className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1 rounded text-sm transition-colors"
                          >
                            Ver Paciente
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <ServicePaymentModal
        service={selectedService}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedService(null);
          setSelectedTab("payment");
        }}
        onUpdate={handleServiceUpdate}
        formatCurrency={formatCurrency}
        defaultTab={selectedTab}
      />
    </div>
  );
};

export default DentalFinancialDashboard;
